
/// <amd-module name="Build/DesignerExtensions/YamlSnippetForm/Sources/SnippetFormSource" />
import { IVssTelemetryService } from "VSS/Platform/Telemetry";
import { IVssPageContext } from "VSS/Platform/Context";
import { ITfsPageService } from "Tfs/Platform/Page";
import * as Utils_String from "VSS/Core/Util/String";

import { AzureSubscription, ServiceEndpoint, ServiceEndpointRequest } from "DistributedTask/Client/Contracts/TaskAgent";
import { ITaskAgentRestClient, getTaskAgentClient } from "DistributedTask/Client/RestClient/TaskAgent";
import { YamlSnippetInput } from "Pipelines/Common/Library/Contracts/DataProvider";
import { IServiceEndpointRestClient, getServiceEndpointClient } from "ServiceEndpoints/Client/RestClient/ServiceEndpoint";
import { AzureEndpointConstants, EndpointAuthorizationSchemes } from "ServiceEndpoints/Client/Constants/ServiceEndpoint";

import { ServiceEndpointDetails, IServiceConnectionProvider } from "Build/DesignerExtensions/YamlAssistantLibrary/Contracts/Types";
import { AzureRMConnectionType, TelemetryConstants, InternalDataSources } from "Build/DesignerExtensions/YamlAssistantLibrary/Contracts/Constants";
import * as Resources from "Build/DesignerExtensions/YamlSnippetForm/Resources";

import { getCookie } from "VSS/Platform/Util/Cookie";

export class SnippetFormSource implements IServiceConnectionProvider {
    private _dtClient: ITaskAgentRestClient;
    private _serviceEndpointClient: IServiceEndpointRestClient;

    private _projectId: string;
    private _telemetryService: IVssTelemetryService;

    private _sleepTimeAwaitingEndpoint: number = 1000; // done this way for l0 tests to manipulate the value

    private _getInputType?: (name: string) => string;

    public constructor(pageContext: IVssPageContext, getInputType?: (name: string) => string) {
        this._dtClient = getTaskAgentClient(pageContext);
        this._serviceEndpointClient = getServiceEndpointClient(pageContext);

        this._telemetryService = pageContext.getService<IVssTelemetryService>("IVssTelemetryService");

        const pageService = pageContext.getService<ITfsPageService>("ITfsPageService");
        this._projectId = pageService.getData()!.project!.id;
        this._getInputType = getInputType;
    }

    public async getAzureSubscriptions(): Promise<AzureSubscription[]> {
        if ($DEBUG && getCookie("YamlServiceDebug") === "true") {
            return Promise.resolve(this._getDebugSubscriptions());
        }

        return this._dtClient.getAzureSubscriptions().then(queryResult => {
            return queryResult.value;
        });
    }
    
    public async getServiceConnections(connectionType: string): Promise<ServiceEndpoint[]> {
        if ($DEBUG && getCookie("YamlServiceDebug") === "true") {
            return Promise.resolve(this._getDebugServiceConnections());
        }

        let authSchemes: string[] | undefined = undefined;
        if (connectionType.indexOf(":") > 0) {
            let splitString = connectionType.split(":");
            connectionType = splitString[0];
            authSchemes = splitString[1].split(",");
        }

        const endPointIds = undefined;
        const includeFailed = undefined;
        return this._dtClient.getServiceEndpoints(this._projectId, connectionType, authSchemes, endPointIds, includeFailed);
    }

    public async createAzureRMConnection(subscription: AzureSubscription): Promise<ServiceEndpoint> {
        // reference https://dev.azure.com/mseng/AzureDevOps/_git/AzureDevOps?path=%2FServiceEndpoints%2FSdk%2FServer%2FAzureEndpointConstants.cs&version=GBmaster
        // until these contants are automatically part of the ServiceEndpoints client

        this._telemetryService && this._telemetryService.publishEvent(TelemetryConstants.Area, TelemetryConstants.createAzureRM, {});

        const endpoint: ServiceEndpoint = {
            authorization: {
                parameters: {
                    authenticationType: AzureEndpointConstants.SpnKeyAuthType,
                    tenantId: subscription.subscriptionTenantId
                },
                scheme: EndpointAuthorizationSchemes.ServicePrincipal
            },
            data: {
                creationMode: AzureEndpointConstants.SpnCreationModeAutomatic,
                environment: AzureEndpointConstants.DefaultAzureEnvironment,
                scopeLevel: AzureEndpointConstants.SubscriptionString,
                subscriptionId: subscription.subscriptionId,
                subscriptionName: subscription.displayName
            },
            type: AzureRMConnectionType,
            name: subscription.displayName + '(' + subscription.subscriptionId + ')'
        } as any as ServiceEndpoint;

        if ($DEBUG && getCookie("YamlServiceDebug") === "true") {
            await this._sleep(2000);
            let newDebugConnection = { ...endpoint, id: "sc_new" + subscription.subscriptionId };
            this._debugServiceConnections.push(newDebugConnection);
            return newDebugConnection;
        }

        const serviceEndpoint = await this._dtClient.createServiceEndpoint(endpoint, this._projectId);

        return this._returnEndpointWhenReady(serviceEndpoint.id);
    }
    
    public async getDataSourceResources(
        dependentInput: YamlSnippetInput,
        endpointToCall: string,
        serviceEndpointDetails?: ServiceEndpointDetails,
        parameters?: { [key: string]: string },
        makeRecursiveDatasourceCalls?: boolean): Promise<{ [key: string]: string }> {

        if (InternalDataSources[dependentInput.type]) {
            endpointToCall = InternalDataSources[dependentInput.type];
        }
        else if (!Utils_String.isGuid(endpointToCall)) {
            // This string doesn't look like an id, so try to resolve it. If that fails, as a last resort
            // just try using whatever we were given
            let resolvedEndpoint = await this._findEndpointByName(endpointToCall, this._getInputType && this._getInputType(dependentInput.dataSourceDependency));
            endpointToCall = resolvedEndpoint || endpointToCall;
        }

        const request = {
            dataSourceDetails: {
                dataSourceName: dependentInput.dataSourceBinding.dataSourceName,
                dataSourceUrl: dependentInput.dataSourceBinding.endpointUrl,
                headers: [],
                resourceUrl: "",
                requestContent: dependentInput.dataSourceBinding.requestContent,
                requestVerb: dependentInput.dataSourceBinding.requestVerb,
                resultSelector: dependentInput.dataSourceBinding.resultSelector,
                initialContextTemplate: dependentInput.dataSourceBinding.initialContextTemplate,
                parameters: {
                    ...dependentInput.dataSourceBinding.parameters,
                    ...parameters
                }
            },
            resultTransformationDetails: {
                resultTemplate: dependentInput.dataSourceBinding.resultTemplate,
                callbackContextTemplate: dependentInput.dataSourceBinding.callbackContextTemplate,
                callbackRequiredTemplate: dependentInput.dataSourceBinding.callbackRequiredTemplate
            },
            serviceEndpointDetails: serviceEndpointDetails
        } as ServiceEndpointRequest;

        if (dependentInput.dataSourceDependency) {
            request.dataSourceDetails.parameters[dependentInput.dataSourceDependency] = endpointToCall;
        }

        let serviceEndpointResult: any[] = [];
        serviceEndpointResult = await this._getServiceEndpointResult(request, endpointToCall, serviceEndpointResult, makeRecursiveDatasourceCalls);
        
        let optionsMap: { [key: string]: string } = {};
        if (serviceEndpointResult && serviceEndpointResult.length > 0) {
            serviceEndpointResult.forEach((option: string) => {
                try {
                    const parsedData = JSON.parse(option);
                    if (parsedData.DisplayValue && parsedData.Value) {
                        optionsMap[parsedData.Value] = parsedData.DisplayValue;
                    }
                    else if (parsedData.id && parsedData.name) {
                        optionsMap[parsedData.id] = parsedData.name;
                    }
                    else {
                        optionsMap[option] = option;
                    }
                } catch (ex) {
                    // If the option is not a valid stringified JSON, use the option as it is.
                    optionsMap[option] = option;
                }
            });
        }

        return optionsMap;
    }

    private async _findEndpointByName(name: string, type: string | undefined): Promise<string | undefined> {
        let result = await this._serviceEndpointClient.getServiceEndpointsByNames(this._projectId, [name], type);
        if (result.length) {
            // I don't currently know why this would come back with more than one. Nor, if it does, how to differentiate
            // Will have to adjust this based on testing and feedback
            if (result.length > 1) {
                this._telemetryService && this._telemetryService.publishEvent(TelemetryConstants.Area, TelemetryConstants.FindEndpointByName, {
                    endpoints: result.map(r => { return {
                        id: r.id,
                        name: r.name,
                        description: r.description,
                        isReady: r.isReady,
                        groupScopeId: r.groupScopeId,
                        type: r.type
                    }})
                });
            }
            return result[0].id;
        }
        else {
            this._telemetryService && this._telemetryService.publishEvent(TelemetryConstants.Area, TelemetryConstants.FindEndpointByName, {
                endpoints: []
            });
            return undefined;
        }
    }

    private async _getServiceEndpointResult(request: any, endpointToCall: string, accumulatedResult: any[], makeRecursiveDatasourceCalls?: boolean): Promise<any[]> {
        if (!makeRecursiveDatasourceCalls) {
            const response = await this._serviceEndpointClient.executeServiceEndpointRequest(request as any, this._projectId, endpointToCall);
            
            // have to parse this rather than compare strings because the response is inconsistent.
            // The property is type string but sometimes it just gives us back a number
            const statusCode = parseInt(response.statusCode);
            if (response.errorMessage && statusCode !== 404) {
                throw new Error(response.errorMessage);
            }

            return response.result;
        }

        const response = await this._serviceEndpointClient.executeServiceEndpointRequest(request as any, this._projectId, endpointToCall);

        // Not throwing if response contains error, as we want to ignore any external server errors,
        // and return whatever result we have from previous successful calls.
        if (!response.errorMessage) {
            // append the result from this call, to the accumulated result
            const result = response.result as any[];
            if (result && result.length > 0) {
                accumulatedResult = accumulatedResult.concat(result);
            }
        }
        else {
            // Throw only when the first call fails
            if (accumulatedResult.length == 0) {
                throw new Error(response.errorMessage);
            }
        }

        if (response.callbackRequired) {
            // pass the callbackContextParameters obtained from previous request as datasource parametes.
            Object.assign(request.dataSourceDetails.parameters, response.callbackContextParameters);
            accumulatedResult = await this._getServiceEndpointResult(request, endpointToCall, accumulatedResult, makeRecursiveDatasourceCalls);
        }

        return accumulatedResult;
    }

    /**
     * We call this after creating an AzureRM connection. The call to create the service connection succeeds right away
     * but the connection isn't actually ready or successful at this point. The only way I've found to know that the
     * connection is ready is polling. So, after creating the connection we poll for 2 minutes; each time testing
     * the connection to find out if it returns 'isReady'
     * 
     * While polling, we sleep in 1 second intervals to not hammer the server and to not block the UI as you can still
     * interact with the form or yaml editor while waiting for this to finish.
     * 
     * If this times out or there is an error, we post that error against the input that caused this creation in the
     * first place (should be an AzureSubscriptionPickList) so that it can potentially handle displaying the
     * error of what happened and allow the user to pick a different subscription or retry.
     */
    private async _returnEndpointWhenReady(endpointId: string): Promise<ServiceEndpoint> {
        let endpointDetails = await this._dtClient.getServiceEndpointDetails(this._projectId, endpointId);
        const maxIterations = 120;
        let currentIteration = 0;
        while (!endpointDetails.isReady) {
            if (endpointDetails.operationStatus && endpointDetails.operationStatus.state === "Failed") {
                throw new Error(endpointDetails.operationStatus.statusMessage);
            }

            if (currentIteration >= maxIterations) {
                throw new Error(Resources.CreateRMTimeout);
            }
            currentIteration++;

            await this._sleep(this._sleepTimeAwaitingEndpoint);

            endpointDetails = await this._dtClient.getServiceEndpointDetails(this._projectId, endpointId);
        }

        return endpointDetails;
    }

    private async _sleep(ms: number) {
        return new Promise((resolve) => setTimeout(resolve, ms));
    }

    // only used for debugging
    private _debugSubscriptions: AzureSubscription[];
    private _debugServiceConnections: ServiceEndpoint[];
    private _getDebugSubscriptions(): AzureSubscription[] {
        if (!this._debugSubscriptions) {
            this._debugSubscriptions = [];
            for (let i = 1; i < 10; ++i) {
                this._debugSubscriptions.push({
                    displayName: "sub " + i,
                    subscriptionId: "" + i,
                    subscriptionTenantId: "tenant" + i,
                    subscriptionTenantName: "tenant " + i
                });
            }
        }
        return this._debugSubscriptions;
    }

    private _getDebugServiceConnections(): ServiceEndpoint[] {
        if (!this._debugServiceConnections) {
            this._debugServiceConnections = [];
            for (let i = 1; i < 10; ++i) {
                this._debugServiceConnections.push({
                    name: "sc " + i,
                    id: "sc" + i
                } as any);
            }
        }
        return this._debugServiceConnections;
    }
}

